/**
 * ForgeID API Integration
 * 
 * Handles communication with ForgeID.app for SBT avatar generation and management
 */

// ForgeID API base URL
const FORGEID_API_BASE = import.meta.env.VITE_FORGEID_URL || 'https://forgeid.app';

// Contract addresses
const FORGEID_CONTRACTS = {
  'polygon-amoy': import.meta.env.VITE_FORGEID_SBT_CONTRACT_AMOY || '0x4100691400F1FC3C63eFdF22ced3Bcb2C5469372',
  'polygon': import.meta.env.VITE_FORGEID_SBT_CONTRACT_MAINNET || '',
};

// Network configuration
const FORGEID_NETWORK = import.meta.env.VITE_FORGEID_NETWORK || 'polygon-amoy';

/**
 * Get ForgeID API endpoint URL
 */
export const getForgeIDApiUrl = (endpoint) => {
  return `${FORGEID_API_BASE}/api${endpoint}`;
};

/**
 * Generate ForgeID URL with return callback
 */
export const getForgeIDGenerateUrl = (returnUrl) => {
  const baseUrl = FORGEID_API_BASE;
  return `${baseUrl}/generate?returnUrl=${encodeURIComponent(returnUrl)}`;
};

/**
 * Parse SBT data from ForgeID callback URL
 */
export const parseForgeIDCallback = (urlParams) => {
  // Handle both URLSearchParams and string
  if (typeof urlParams === 'string') {
    urlParams = new URLSearchParams(urlParams);
  }
  if (!(urlParams instanceof URLSearchParams)) {
    // If it's an object, try to convert
    if (typeof urlParams === 'object' && urlParams !== null) {
      urlParams = new URLSearchParams(urlParams);
    } else {
      console.error('Invalid urlParams type:', typeof urlParams);
      return {
        contractAddress: null,
        tokenId: null,
        tokenUri: null,
        imageUrl: null,
        network: FORGEID_NETWORK,
      };
    }
  }
  
  return {
    contractAddress: urlParams.get('contract') || urlParams.get('contractAddress'),
    tokenId: urlParams.get('tokenId') || urlParams.get('token_id'),
    tokenUri: urlParams.get('tokenUri') || urlParams.get('token_uri'),
    imageUrl: urlParams.get('image') || urlParams.get('imageUrl'),
    network: urlParams.get('network') || FORGEID_NETWORK,
  };
};

/**
 * Get user's SBT avatars from ForgeID API
 * 
 * @param {string} walletAddress - User's wallet address
 * @param {string} network - Network (polygon-amoy, polygon)
 * @returns {Promise<Array>} Array of SBT avatars
 */
export const getUserSBTAvatars = async (walletAddress, network = FORGEID_NETWORK) => {
  try {
    // For now, we'll query on-chain directly
    // In the future, ForgeID might have an API endpoint for this
    return {
      data: [],
      error: null,
    };
  } catch (error) {
    console.error('Error fetching user SBT avatars:', error);
    return {
      data: null,
      error: error.message,
    };
  }
};

/**
 * Get SBT metadata from token URI
 * 
 * @param {string} tokenUri - IPFS or HTTP URI
 * @returns {Promise<Object>} SBT metadata
 */
export const getSBTMetadata = async (tokenUri) => {
  if (!tokenUri) {
    return { data: null, error: 'Token URI is required' };
  }

  try {
    // Handle IPFS URIs
    let url = tokenUri;
    if (tokenUri.startsWith('ipfs://')) {
      const cid = tokenUri.replace('ipfs://', '');
      url = `https://ipfs.io/ipfs/${cid}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch metadata: ${response.statusText}`);
    }

    const metadata = await response.json();
    return { data: metadata, error: null };
  } catch (error) {
    console.error('Error fetching SBT metadata:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Verify SBT ownership on-chain
 * 
 * @param {string} contractAddress - SBT contract address
 * @param {string} tokenId - Token ID
 * @param {string} walletAddress - Wallet address to verify
 * @param {string} network - Network name
 * @returns {Promise<boolean>} True if wallet owns the SBT
 */
export const verifySBTOwnership = async (contractAddress, tokenId, walletAddress, network = FORGEID_NETWORK) => {
  if (!contractAddress || !tokenId || !walletAddress) {
    return false;
  }

  try {
    // This will be implemented using wagmi/viem
    // For now, return true if contract address matches known ForgeID contracts
    const knownContract = FORGEID_CONTRACTS[network];
    if (contractAddress.toLowerCase() === knownContract?.toLowerCase()) {
      // In production, verify on-chain using wagmi
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error verifying SBT ownership:', error);
    return false;
  }
};

/**
 * Get contract address for network
 */
export const getContractAddress = (network = FORGEID_NETWORK) => {
  return FORGEID_CONTRACTS[network] || FORGEID_CONTRACTS['polygon-amoy'];
};

/**
 * Get PolygonScan URL for SBT
 */
export const getPolygonScanUrl = (contractAddress, tokenId, network = FORGEID_NETWORK) => {
  const baseUrl = network === 'polygon' 
    ? 'https://polygonscan.com'
    : 'https://amoy.polygonscan.com';
  
  return `${baseUrl}/token/${contractAddress}?a=${tokenId}`;
};

/**
 * Format SBT display data
 */
export const formatSBTData = (sbt) => {
  return {
    contractAddress: sbt.contractAddress || sbt.contract_address,
    tokenId: sbt.tokenId || sbt.token_id,
    tokenUri: sbt.tokenUri || sbt.token_uri,
    imageUrl: sbt.imageUrl || sbt.image_url,
    network: sbt.network || 'polygon-amoy',
    name: sbt.name || `SBT #${sbt.tokenId || sbt.token_id}`,
    description: sbt.description || 'Soulbound Avatar',
  };
};

